import { isAnalyticsDateFilterTypeName } from './dateFilterInputAdapters'

/** GraphQL filter input type for Prisma / subgraph string filters. */
export const STRING_FILTER_INPUT = 'StringFilterInput'

/** GraphQL filter input type for analytics cube string filters. */
export const CUBE_STRING_FILTER = 'CubeStringFilter'

const STRING_FILTER_TYPE_NAMES = new Set([
  STRING_FILTER_INPUT,
  CUBE_STRING_FILTER,
])

/** True when the type name is a known string filter input (Prisma or cube). */
export const isStringFilterTypeName = (
  typeName: string | undefined | null,
): boolean => (typeName ? STRING_FILTER_TYPE_NAMES.has(typeName) : false)

/**
 * Default filter condition key when a field is selected and no existing value
 * or ofType.state.initialValue is present.
 *
 * - string filters (StringFilterInput, CubeStringFilter) → `in` (Is Any Of)
 * - analytics date filters → `inRange`
 * - everything else → `equals`
 */
export const getDefaultFilterCondition = (
  typeName: string | undefined | null,
): string => {
  if (isStringFilterTypeName(typeName)) return 'in'
  if (isAnalyticsDateFilterTypeName(typeName)) return 'inRange'
  return 'equals'
}

/**
 * Prisma StringFilterInput accepts `mode`; CubeStringFilter does not.
 * Adding mode to cube filters causes GraphQL validation errors and breaks widgets.
 * When the type is not Prisma string filter, any existing `mode` is stripped.
 */
export const withOptionalStringFilterMode = (
  value: Record<string, unknown>,
  filterTypeName: string | undefined | null,
): Record<string, unknown> => {
  if (filterTypeName === STRING_FILTER_INPUT) {
    return { ...value, mode: 'insensitive' }
  }
  const { mode: _mode, ...rest } = value
  return rest
}

/**
 * CubeStringFilter expects list operands for contains/startsWith/endsWith
 * (and their not* variants). Prisma StringFilterInput uses scalars.
 */
export const CUBE_STRING_LIST_OPERATORS = new Set([
  'contains',
  'notContains',
  'startsWith',
  'notStartsWith',
  'endsWith',
  'notEndsWith',
])

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const asCubeStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string')
  }
  if (typeof value === 'string') return [value]
  return []
}

/**
 * Rewrite Prisma-style nested `not` / null-equals into CubeStringFilter keys.
 * CubeStringFilter has no nested `not` — it uses `set`, `notEquals`, `notContains`, etc.
 */
const rewritePrismaStringOpsToCube = (
  rest: Record<string, unknown>,
): Record<string, unknown> | null => {
  const keys = Object.keys(rest)
  if (keys.length === 1 && rest.equals === null) {
    return { set: false }
  }
  if (keys.length === 1 && isPlainObject(rest.not)) {
    const not = rest.not
    if (not.equals === null) {
      return { set: true }
    }
    if (typeof not.equals === 'string') {
      return { notEquals: not.equals }
    }
    if (not.contains !== undefined) {
      return { notContains: asCubeStringList(not.contains) }
    }
    if (not.startsWith !== undefined) {
      return { notStartsWith: asCubeStringList(not.startsWith) }
    }
    if (not.endsWith !== undefined) {
      return { notEndsWith: asCubeStringList(not.endsWith) }
    }
  }
  return null
}

/**
 * Normalize a single field filter operand for cube GraphQL:
 * - strip Prisma-only `mode`
 * - rewrite blank / present / notEqual / notContains into CubeStringFilter keys
 * - wrap scalar list-operators as single-element arrays
 */
export const normalizeCubeStringFilterOperand = (operand: unknown): unknown => {
  if (
    operand == null ||
    typeof operand !== 'object' ||
    Array.isArray(operand)
  ) {
    return operand
  }
  const source = operand as Record<string, unknown>
  const { mode: _mode, ...rest } = source

  const rewritten = rewritePrismaStringOpsToCube(rest)
  if (rewritten) {
    return rewritten
  }

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(rest)) {
    if (CUBE_STRING_LIST_OPERATORS.has(key) && typeof value === 'string') {
      result[key] = [value]
      continue
    }
    if (key === 'not' && isPlainObject(value)) {
      // Nested `not` is invalid on CubeStringFilter; drop rather than forward.
      continue
    }
    result[key] = value
  }
  return result
}

/**
 * Strip Prisma-only keys (and normalize cube string list ops) from a
 * WhereInput-shaped filter object before sending to cube queries / fan-out.
 */
export const sanitizeFilterForCube = (
  filter: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(filter)) {
    if (key === 'AND' || key === 'OR') {
      if (Array.isArray(value)) {
        result[key] = value.map(item =>
          item != null && typeof item === 'object' && !Array.isArray(item)
            ? sanitizeFilterForCube(item as Record<string, unknown>)
            : item,
        )
      }
      continue
    }
    result[key] = normalizeCubeStringFilterOperand(value)
  }
  return result
}
